/**
 * # Side Fixed Panel
 *
 * @example
 * new Spark.SideFixedPanel();
 *
 * @module components/side-fixed-panel.js
 * @since 3.4.0
 */

import BaseComponent from './base';
import SideFixedDock from './side-fixed-dock';
import Underlay from './underlay';

import { get as getBreakpoint } from '../helpers/dom/breakpoint';

import getParent from '../helpers/traversal/get-parent';

import mixin from '../helpers/util/mixin';

import events from '../mixins/events';

/**
 * Side Fixed Panel
 * @class creates a Side Fixed Panel
 * @extends {BaseComponent}
 */
class SideFixedPanel extends BaseComponent {

  /**
   * SideFixedPanel constructor.
   * @param {Element} el base DOM element
   * @param {Object} params
   * @constructor
   */
  constructor(el, params = {}) {

    super(el, params);

    if (params.position) this.position = params.position;
    if (params.toggleEl) this._toggleEl = params.toggleEl;

    this._bindEventListenerCallbacks();
    this._addEventListeners();
  }


  /**
   * Sets up references
   * @param  {Element} el base DOM element
   * @private
   */
  _cacheElements(el) {
    this.el = el;
    this.closeButton = this.el.querySelector('.spark-panel--fixed__close');
    this.underlay = new Underlay({
      disabled: false,
      display: false,
      style: 'dark'
    });

    let parentEl = getParent(this.el, 'body') || document.body;
    let dock = parentEl.querySelector(
      '.spark-panel-dock[data-position="' + this.position + '"], .spark-panel-dock--condensed[data-position="' + this.position + '"]'
    );

    if (dock && (dock.parentNode.querySelector('.spark-panel--fixed') !== this.el)) dock = undefined;

    let condensedPanelEl = undefined;

    /// If Dock markup was found, or this is a push variation, generate a secondary SFP that's cover for when at small screen sizes
    if (dock || this.push) {
      condensedPanelEl = document.createElement('aside');
      condensedPanelEl.classList.add('spark-panel--fixed');
      condensedPanelEl.classList.add('spark-panel--fixed--condensed');
      condensedPanelEl.setAttribute('aria-expanded', false);
      condensedPanelEl.setAttribute('tabindex', '-1');
      condensedPanelEl.dataset.position = this.position;
      condensedPanelEl.dataset.placement = 'cover';
      condensedPanelEl.id = this.el.id.concat('--alt');
    }

    const self = this;

    if (dock) {

      // If docked, intiate the generated SFP on the Dock
      document.body.appendChild(condensedPanelEl);
      this.dock = new SideFixedDock(dock, {
        sideFixedPanel: this,
        condensedPanel: new SideFixedPanel(condensedPanelEl, {
          _parentPanel: this,

          // On close, return contents back to self
          onClose: () => {
            const panel = self.dock.condensedPanel.el.firstElementChild;
            panel.hidden = true;
            self.display = '';
            self.el.appendChild(panel);
            self.dock.clearSelected();
          }
        })
      });

      this._onResize();
    } else if (this.push) {

      // If stanadard push, initiate the generated SFP on self
      document.body.appendChild(condensedPanelEl);
      this.condensedPanel = new SideFixedPanel(condensedPanelEl, {
        _parentPanel: this,

        // on Close, return contents back to self
        onClose: () => {
          const panel = self.condensedPanel.el.firstElementChild;
          self.display = '';
          self.el.appendChild(panel);
        }
      });
    }
  }


  /**
   * Create bound versions of event listener callbacks and store them.
   * Otherwise we can't unbind from these events later because the
   * function signatures won't match.
   * @private
   */
  _bindEventListenerCallbacks() {
    this._onUnderlayDismissBound = this._onUnderlayDismiss.bind(this);
    this._onDismissBound = this._onDismiss.bind(this);
    this._onClickBound = this._onClick.bind(this);
    this._onTransitionEndBound = this._onTransitionEnd.bind(this);
    this._onResizeBound = this._onResize.bind(this);
    this._onKeyDownBound = this._onKeyDown.bind(this);
    this._onOpenBound = this._onOpen.bind(this);
    this._onCloseBound = this._onClose.bind(this);
    this._onToggleBound = this._onToggle.bind(this);
    this._toggleBound = this.toggle.bind(this);
  }


  /**
   * Add event listeners for DOM events.
   * @private
   */
  _addEventListeners() {
    this.on('dismiss', this._onDismissBound);
    this.on('toggle', this._onToggleBound);
    this.el.addEventListener('click', this._onClickBound, true);
    this.el.addEventListener('transitionend', this._onTransitionEndBound, true);
    this.el.addEventListener('keydown', this._onKeyDownBound, true);

    this.underlay.on('dismiss', this._onUnderlayDismissBound);

    window.addEventListener('resize', this._onResizeBound, true);

    if (this.toggleEl) this.toggleEl.addEventListener('click', this._toggleBound, true);
  }


  /**
   * Remove event listeners for DOM events..
   * @private
   */
  _removeEventListeners() {
    this.off('dismiss', this._onDismissBound);
    this.off('toggle', this._onToggleBound);
    this.el.removeEventListener('click', this._onClickBound, true);
    this.el.removeEventListener('transitionend', this._onTransitionEndBound, true);
    this.el.removeEventListener('keydown', this._onKeyDownBound, true);

    this.underlay.off('dismiss', this._onUnderlayDismissBound);

    window.removeEventListener('resize', this._onResizeBound, true);

    if (this.toggleEl) this.toggleEl.removeEventListener('click', this._toggleBound, true);
  }


  /**
   * On Resize, adjust Panel's position, and possibly width depending on new screen size
   * @private
   */
  _onResize() {
    this.mediaWidth = window.innerWidth;

    if (this.dock && this.mediaWidth <= this.condensedBreakpoint && !this.dock.condensed) {
      this.dock.condense();
    } else if (this.dock && this.mediaWidth > this.condensedBreakpoint && this.dock.condensed) {
      if (this.dock.condensedPanel.active) {
        const self = this;
        const panelId = this.dock.condensedPanel.el.firstElementChild.id;
        const dockItem = this.dock.el.querySelector('[aria-controls=' + panelId + ']');
        this.dock.condensedPanel.close().then(() => {
          self.dock.expand();
          dockItem.focus();
          dockItem.click();
        });
      } else {
        this.dock.expand();
      }
    } else if (this.dock && this.mediaWidth > this.condensedBreakpoint && !this.dock.condensed) {
      if (this.cover && this.active) {
        this.close();
      }
    }

    if (this.cover) {

      this.currentBreakpoint = getBreakpoint(this.mediaWidth);

      if (this.currentBreakpoint === this.mobileBreakpoint || this.mediaWidth <= this.mobileBreakpoint) {
        this.el.style.minWidth = '100%';
        this.el.setAttribute('data-display-fullwidth', '');

        if (this.position === 'right') {
          this.el.style.left = '100%';
          this.el.style.right = 'auto';
          this.translateX = -this.width;
        } else if (this.position === 'left') {
          this.el.style.left = 'auto';
          this.el.style.right = '100%';
          this.translateX = this.width;
        }
      } else {
        this.el.style.minWidth = '';
        this.el.removeAttribute('data-display-fullwidth');

        if (this.active && this.position === 'right') {
          this.translateX = -this.width;
        } else if (this.active && this.position === 'left') {
          this.translateX = this.width;
        }
      }
    }

    if (this.push && this.mediaWidth <= this.condensedBreakpoint && this.active) {
      const self = this;
      this.close().then(() => {
        this.open().catch(() => {
          self.display = 'open';
        });
      }).catch(() => {
        self.display = '';
      });
    } else if (this.cover && this.active && this._parentPanel) {

      if (this.mediaWidth > this._parentPanel.condensedBreakpoint) {
        const self = this;

        self.close().then(() => {
          self._parentPanel.open().catch(() => {
            self._parentPanel.display = 'open';
          });
        }).catch(() => {
          self.display = '';
        });
      }
    }

  }


  /**
   * On Key Down (ENTER|ESC), close panel / On Key Down (TAB), ensure focus stays or goes where necessary
   * @param {Event} e
   * @private
   */
  _onKeyDown(e) {
    const TAB_KEY_CODE = 9;
    const ENTER_KEY_CODE = 13;
    const ESC_KEY_CODE = 27;

    switch (e.keyCode) {
      case ENTER_KEY_CODE:
        if (e.target === this.closeButton || e.target.classList.contains('spark-panel--fixed__close')) this.close();
        break;
      case ESC_KEY_CODE:
        this.close();
        break;
      case TAB_KEY_CODE: {
        if (!this.underlay.active) {
          return;
        }

        const focusable = this.el.querySelectorAll('a[href]:not([tabindex^="-"]):not([disabled]), area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]');

        if (focusable.length > 0) {
          const focusableElements = Array.prototype.slice.call(focusable);

          if (focusableElements.length === 1) {
            e.preventDefault();
          } else if (e.shiftKey && document.activeElement === focusableElements[0]) {
            e.preventDefault();
            focusableElements[focusableElements.length - 1].focus();
          } else if (!e.shiftKey && document.activeElement === focusableElements[focusableElements.length - 1]) {
            e.preventDefault();
            focusableElements[0].focus();
          }
        }
        break;
      }
      default:
        return;
    }
  }


  /**
   * If an active underlay associated with this panel is dismissed,
   * then also dismiss the panel
   * @private
   */
  _onUnderlayDismiss() {
    this.trigger('dismiss');
  }


  /**
   * On Dismiss, close Panel
   * @param {Object} e
   * @private
   */
  _onDismiss(e) {
    this.close();
    this.onDismiss.call(this, e);
  }


  /**
   * On Toggle, trigger callbacks
   * @param {Object} e
   * @private
   */
  _onToggle(e) {
    this.onToggle.call(this, e);
  }


  /**
   * On Click, dismiss panel if target was a `.spark-panel--fixed__close` class close button
   * @param {Object} e
   * @private
   */
  _onClick(e) {
    if (e.target === this.closeButton || e.target.classList.contains('spark-panel--fixed__close')) this._onDismiss(e);
  }


  /**
   * On Transition End, the panel has either finished opening or closing,
   * so pending the current value of `[data-display]`, trigger the proper event ("open" or "close")
   * @private
   */
  _onTransitionEnd() {

    // If `[data-display]` currently reads "show",
    // then the panel just finished "opening", so trigger an "open" event
    if (this.display === 'show') this._onOpenBound();

    // If `[data-display]' currently reads "hide",
    // then the panel just finished "closing", so trigger a "close" event
    if (this.display === 'hide') this._onCloseBound();
  }


  /**
   * On Open, the panel has just finished it's animation "open",
   * so retrieve stored `resolve` method in order to close pending promise,
   * update the DOM and focus state, then run `onOpen()` callback
   * @private
   */
  _onOpen(e) {

    // Change `[data-display]` to "open", to reflect that the panel has finished transitioning and is now open
    this.display = 'open';

    // Since the panel is now "open", make sure the proper `aria` attributes reflect that
    this.el.setAttribute('aria-expanded', true);

    // Verify a `resolve` method exists to be called, before blindly calling on it
    if (typeof this._resolve === 'function') {

      // Resolve the currently pending promise
      this._resolve();

      // Clear the currently stored `resolve` method, so it's not used again
      this._resolve = undefined;
    }

    this.trigger('open');

    // If panel has a close button inside (`.spark-panel--fixed__close`), focus on it
    if (this.closeButton) this.closeButton.focus();

    // Run `onOpen()` callback, while passing on the "onOpen" event object
    this.onOpen.call(this, e);
  }


  /**
   * On Close, the panel has just finished it's animation "closed",
   * so retrieve stored `resolve` method in order to close pending promise,
   * update the DOM and focus state, then run `onClose()` callback
   * @private
   */
  _onClose(e) {

    // Change `[data-display]` to "", to reflect that the panel has finished transitioning and is now closed
    this.display = '';

    // Since the panel is now "closed", make sure the proper `aria` attributes reflect that
    this.el.setAttribute('aria-expanded', false);

    // Verify a `resolve` method exists to be called, before blindly calling on it
    if (typeof this._resolve === 'function') {

      // Resolve the currently pending promise
      this._resolve();

      // Clear the currently stored `resolve` method, so it's not used again
      this._resolve = undefined;
    }

    // If panel is a "cover" type, and not "docked", then all it's value's can be reset
    if (this.cover) {
      if (!this.el.classList.contains('spark-panel--docked') && !this.dock) {

        // Make sure panel is hidden, so that as we reset it's position values,
        // it doesn't accidently cause any bugs
        this.el.style.display = 'none';

        // Now that the panel is hidden, any `transform: translateX()` values,
        // used to transition the panel open/closed may be reset
        this.translateX = 0;

        // Values for CSS position properties `left` and `right` can also be reset
        this.el.style.left = '';
        this.el.style.right = '';

        // Now that positions have been reset, panel should be off screen,
        // and we can clear any values `transform` or `display` from this.el's `style` attribute
        this.el.style.transform = '';
        this.el.style.display = '';
      }
    }

    // If this panel is "docked"
    if (this.dock) {

      // Determine which side fixed dock item was used to open it
      const dockItem = this.dock.el.querySelector('[aria-expanded="true"]');

      // Then return focus to that dock item, now that the panel is closed again
      if (dockItem) dockItem.focus();

      // Also update the dock's DOM, so that no dock items show "open panel" states,
      // and `aria` attributes are correct
      this.dock.clearSelected();
    }

    this.trigger('close');

    // If an element was provided as a toggle, now that the panel is closed,
    // update the toggle's DOM so `aria` attributes are correct, then focus on it
    if (this.toggleEl) {
      this.toggleEl.setAttribute('aria-expanded', false);
      this.toggleEl.focus();
    }

    // Run `onClose()` callback, while passing on the "onClose" event object
    this.onClose.call(this, e);
  }


  /**
   * Position setter
   * @param {String} value
   * @public
   */
  set position(value = 'right') {
    value = value.toLowerCase();
    if (this.el && ['right', 'left'].includes(value)) {

      if (this.display === 'open' || this.display === 'show') {
        if (value === 'right') {
          this.el.style.left = 'auto';
          this.el.style.right = '0px';
        } else if (value === 'left') {
          this.el.style.left = '0px';
          this.el.style.right = 'auto';
        }
      } else {
        if (value === 'right') {
          this.el.style.left = '100%';
          this.el.style.right = 'auto';
        } else if (value === 'left') {
          this.el.style.left = 'auto';
          this.el.style.right = '100%';
        }
      }

      this.el.dataset.position = value;

      if (this.display === 'open' || this.display === 'show') {
        this.open().catch(() => {});
      }
    }
  }


  /**
   * Position getter
   * @return {String} returns current position
   * @public
   */
  get position() {
    return this.el.dataset.position;
  }


  /**
   * Returns whether or not panel is a cover variant
   * @return {Boolean}
   * @public
   */
  get cover() {
    return this.el.dataset.placement === 'cover';
  }


  /**
   * Returns whether or not panel is a push variant
   * @return {Boolean}
   * @public
   */
  get push() {
    return this.el.dataset.placement === 'push';
  }


  /**
   * `[data-display]` setter, accepts values of 'show', 'open', 'hide', or ''
   * @param {String} value
   * @public
   */
  set display(value) {
    const supportedDisplays = [
      '',
      'open',
      'show',
      'hide'
    ];

    if (supportedDisplays.includes(value)) {
      this.el.dataset.display = value;
    } else if (typeof value === 'boolean') {
      this.el.dataset.display = value ? 'show' : 'hide';
    } else if (value === undefined || value === null) {
      this.el.dataset.display = '';
    }
  }


  /**
   * Returns the current value of `[data-display]`
   * @return {String}
   * @public
   */
  get display() {
    return this.el ? this.el.dataset.display : undefined;
  }


  /**
   * Setter for adjusting current translateX value
   * @param  {Number} px Pixels
   * @private
   */
  set translateX(px) {
    if (!Number.isInteger(px) || px === 0) {
      this.el.style.transform = 'translateX(0)';
    } else {
      this.el.style.transform = 'translateX('.concat(px, 'px)');
    }
  }


  /**
   * Getter that returns the current width of the panel
   * @return {Number} px
   * @private
   */
  get width() {
    return this.el ? Math.round(this.el.scrollWidth) : 0;
  }


  /**
   * Returns whether or not panel is open or closed
   * @return {Boolean}
   * @public
   */
  get active() {
    if (this.condensedPanel && this.condensedPanel.display !== undefined && this.condensedPanel.display !== '') {
      return true;
    } else if (this.display !== undefined && this.display !== '') {
      return true;
    } else {
      return false;
    }
  }


  /**
   * Return Toggle Element if one exists
   * @return {Element}
   */
  get toggleEl() {
    return this._toggleEl ? this._toggleEl : undefined;
  }


  /**
   * Designate an element to be used for toggling panel's open/close state
   * @param  {Element}
   */
  set toggleEl(el) {
    if (el && el.nodeType === 1) {
      if (this._toggleEl) this._toggleEl.removeEventListener('click', this._toggleBound, true);
      this._toggleEl = el;

      if (!el.hasAttribute('aria-expanded')) el.setAttribute('aria-expanded', this.active);
      if (!el.hasAttribute('aria-label')) el.setAttribute('aria-label', 'Expand or Collapse Panel');
      el.addEventListener('click', this._toggleBound, true);
      this.on('close', this._focusToggleBound);
    } else {
      if (this._toggleEl) this._toggleEl.removeEventListener('click', this._toggleBound, true);
      this._toggleEl = undefined;
    }
  }


  /**
   * Toggle the panel's open/closed state
   * @public
   */
  toggle() {
    const self = this;
    const toggle = this.toggleEl;
    switch (this.display) {
      case 'show':
      case 'open':
        this.close().then(() => {
          if (toggle) {
            toggle.setAttribute('aria-expanded', false);
            toggle.focus();
          }
          self.trigger('toggle');
        }).catch(() => {});
        break;
      case 'hide':
      default:
        this.open().then(() => {
          if (toggle) {
            toggle.setAttribute('aria-expanded', true);
          }
          self.trigger('toggle');
        }).catch(() => {});
        break;
    }
  }


  /**
   * Open Panel
   * @param  {Object} options
   * @return {Promise}
   * @public
   */
  open(options = {}) {

    // Store reference to self for use within promise
    const self = this;

    // Make sure the panel's scroll is reset to the top before opening it anew
    if (this.scrollTop) this.el.scrollTop = 0;

    // If panel is a "cover" type but not docked, it may explicitly have `display: none;` set,
    // if it's been opened and closed before, so if so, clear current value for `display`
    if (this.cover && !this.el.classList.contains('spark-panel--docked') && !this.dock) this.el.style.display = '';

    // Resolves on the complete end of the transition event triggered by adjusting `transform: translateX()`
    // [data-display] values control transitions, 'open' and '' are anchors and transitionless,
    // while 'show' and 'hide' will move between 'open' & '', and trigger transition events
    return new Promise(resolve => {

      // Store a reference to `resolve` outside of the method, in case we need to resolve the promise from a different method
      self._resolve = resolve;


      // If SFP is type "cover" and is in the process of showing ('show'), or already showing ('open')
      if (self.cover && self.display === 'show' || self.display === 'open') {

        // If `position` was passed in as option param, that position value is different from the current position value,
        // and this is not a "docked" variation of SFP
        if (options.position && options.position !== self.position && !self.dock) {

          // Clear any currently stored references to promise resolve methods
          self._resolve = undefined;

          // Close SFP
          self.close().then(() => {

            // Then...
            // Store a reference to _this_ promises `resolve`, and update the position to the value passed in
            self._resolve = resolve;
            self.position = options.position;

            // Check to see if `options.underlay: true|false` was provided
            if (options.underlay === true || options.underlay === undefined) {

              // If underlay not disabled, configure it for use with SFP, then show it
              self.underlay.configure({
                disabled: false,
                style: 'dark'
              }).then(self.underlay.show);
            }

            // Make sure SFP has been hidden with a `[data-display]` value of '',
            // so that setting 'show' will trigger an animated opening transition,
            // then set to 'show' to trigger that transition
            self.display = '';
            self.display = 'show';

            // Depending on the current `position` value,
            // properly set CSS `left` or `right` position properties,
            // so that the SFP will be pushed entirely off screen, but resting right on the edge

            if (self.position === 'right') {

              // If the position is "right"

              // Set `left` to `100%` (viewport width units),
              // then use width of SFP to set new `transform: translateX()` value,
              // in order to trigger animated transition
              self.el.style.left = '100%';
              self.translateX = -self.width;
            } else if (self.position === 'left') {

              // If the position is "left"

              // Set `right` to `100%` (viewport width units),
              // then use width of SFP to set new `transform: translateX()` value,
              // in order to trigger animated transition
              self.el.style.right = '100%';
              self.translateX = self.width;
            }
          });

        // Otherwise, `position` either wasn't provided, or wasn't changed,
        // so there's no need to first close SFP
        } else {

          // SFP's current `[data-display]` is still either 'show' or 'open' however,
          // so it's either already in the process of opening, or just already open,
          // so clear any outside reference to `resolve`, and just `resolve` immediatley
          self._resolve = undefined;
          resolve();
        }
        return;

      // Else, SFP is either hidden (`[data-display]` is set to `''`),
      // or it's in the proccess of hiding (`[data-display]` is set to `'hide'`),
      // If it's also a "cover" type
      } else if (self.cover) {

        // Check to see if a `position` param was provided, and if one was,
        // use it to update SFP's current `position` value before doing anything to start opening panel
        if (options.position) self.position = options.position;

        // Check to see if underlay was disabled or not,
        // and verify that this "cover" SFP is not also a "docked" "cover" SFP
        if (options.underlay === true || options.underlay === undefined && !self.el.classList.contains('spark-panel--docked')) {

          // If underlay not disabled, and SFP isn't "docked",
          // configure underlay for use with SFP, then show it
          self.underlay.configure({
            disabled: false,
            style: 'dark'
          }).then(self.underlay.show);
        }

        // If not a "docked" "cover" SFP
        if (!self.dock) {

          // Clear any existing values for CSS position properties `left` & `right`
          self.el.style.left = '';
          self.el.style.right = '';

          // If SFP's current is set to `right`
          if (self.position === 'right') {

            // Set `transform: translateX()` to the current width of the SFP,
            // in order to trigger an animated transition where the entire SFP slides onto the screen
            // (Also, make it a negative value, so that it slides from right -> left)
            self.translateX = -self.width;
          } else if (self.position === 'left') {

            // Set `transform: translateX()` to the current width of the SFP,
            // in order to trigger an animated transition where the entire SFP slides onto the screen
            // (Using a positive value, so that it slides from left -> right)
            self.translateX = self.width;
          }
        }

        // If "cover" SFP _is_ "docked", then CSS position values `left` and `right` need to be updated,
        // but the current `width`` of the `dock` needs to be factored in so that as the panel slides onto
        // the screen, instead of sliding to the edge of the screen, it slides to the inner edge of the dock
        if (self.dock) {

          // If current `position` is "right"
          if (self.position === 'right') {

            // Use CSS position value `left` with `calc()`, `100%` (viewport width units),
            // and the current `dock` `width`, to position SFP directly below it's dock, on the inner edge
            self.el.style.left = 'calc(100% - ' + self.dock.width + 'px)';

            // Make sure any previous values set to CSS position `right` are cleared,
            // for our `left` adjustments to work properly
            self.el.style.right = '';

            // Trigger the transition on screen by adjusting the `transform: translateX()` value,
            // to the `width`` of the SFP (With a negative value, so that it slides from right -> left)
            self.translateX = -self.width;

          // Else, if the current `position` is "left"
          } else if (self.position === 'left') {

            // Make sure any previous values set to CSS position `left` are cleared,
            // for our `right` adjustments to work properly
            self.el.style.left = '';

            // Use CSS position value `right` with `calc()`, `100%` (viewport width units),
            // and the current `dock` `width`, to position SFP directly below it's dock, on the inner edge
            self.el.style.right = 'calc(100% - ' + self.dock.width + 'px)';

            // Trigger the transition on screen by adjusting the `transform: translateX()` value,
            // to the `width`` of the SFP (Using a positive value, so that it slides from left -> right)
            self.translateX = self.width;
          }
        }

        // Explicitly set the `[data-display]` value to "show", so that after the transition has ended,
        // the `onTransitionEnd` callback will be able to determine whether or not,
        // the panel just finished "opening" or "closing", so that it may clean up properly
        self.display = 'show';
      }

      // If SFP is "push" type, the only thing we have to adjust in order to trigger an animated transition,
      // is to change the value of `[data-display]`
      //
      // If `[data-display]` is currently "show" or "open", then the panel is either already open,
      // or in the process of opening
      if (self.push && self.display === 'show' || self.display === 'open') {

        if (window.innerWidth <= this.condensedBreakpoint && this.condensedPanel) {
          self.close().then(() => {
            self.condensedPanel.el.appendChild(self.el.firstElementChild);
            self.condensedPanel.open({underlay: false}).then(() => {
              if (self.condensedPanel.el.querySelector('.spark-panel--fixed__close')) {
                self.condensedPanel.el.querySelector('.spark-panel--fixed__close').focus();
              }
              if (typeof self._resolve === 'function') {
                self._resolve();
                self._resolve = undefined;
              }
            }).catch(() => {
              self.display = '';
              self.condensedPanel.display = 'open';
            });
          }).catch(() => {
            self.display = '';
            self._onClose();
          });
        } else {

          // So snap the panel closed, in order to get to a state which we can "open" the panel _again_
          self.display = '';

          // Then set `[data-display]` to "show", in order to animate the panel open again
          self.display = 'show';
        }

      // Else, if still  a "push" type but not already open or opening
      } else if (self.push) {

        if (window.innerWidth <= this.condensedBreakpoint && this.condensedPanel) {
          self.condensedPanel.el.appendChild(self.el.firstElementChild);
          self.condensedPanel.open({underlay: false}).then(() => {
            if (self.condensedPanel.el.querySelector('.spark-panel--fixed__close')) {
              self.condensedPanel.el.querySelector('.spark-panel--fixed__close').focus();
            }
            self._resolve();
            self._resolve = undefined;
          });

        } else {

          // Then trigger the panel's animated "open" transition, by changing `[data-display]` from '' or 'hide' to 'show'
          self.display = 'show';
        }
      }
    });
  }


  /**
   * Close Panel
   * @return {Promise}
   * @public
   */
  close() {
    const self = this;
    if (this.cover && !this.el.classList.contains('spark-panel--docked') && !this.dock) this.el.style[this.position] = '-'.concat(this.width, 'px');

    // Resolves on the complete end of the transition event triggered by adjusting `transform: translateX()`
    // [data-display] values control transitions, 'open' and '' are anchors and transitionless,
    // while 'show' and 'hide' will move between 'open' & '', and trigger transition events
    return new Promise(resolve => {

      // If "cover" type, but no current value for `[data-display]`,
      // then display is '', and the panel is already closed
      if (self.cover && !self.display) {

        // So `resolve()` promise immediately, and `return` out of `close()` function
        resolve();
        return;
      }

      // If we're still in the method, store an external reference to this promise's `resolve` method
      self._resolve = resolve;

      // If panel has an underlay, hide it
      if (self.underlay.active) self.underlay.hide();

      // If "cover" type and "docked"
      if (self.cover && self.dock) {

        // Clear value for `transform: translateX()`, triggering a transition
        self.translateX = 0;

      // Else, if "cover" type but not "docked"
      } else if (self.cover) {

        // Clear CSS position values for `left` and `right`,
        // so that we don't have clear them below
        self.el.style.left = '';
        self.el.style.right = '';

        // If the current `position` is "right"
        if (self.position === 'right') {

          // Then the CSS position `left` property needs to be set to `100%` (viewport width units),
          // so that when the panel transitions, it ends up off screen to the right
          self.el.style.left = '100%';

          // Now that `left` and `right` are properly set, set `transform: translateX()`,
          // (which should currently be set either to a positive or negative value, equal to the width of the panel in px),
          // to trigger the animated "closing" transition
          self.translateX = 0;
        } else if (self.position === 'left') {

          // Then the CSS position `right` property needs to be set to `100%` (viewport width units),
          // so that when the panel transitions, it ends up off screen to the left
          self.el.style.right = '100%';

          // Now that `left` and `right` are properly set, set `transform: translateX()`,
          // (which should currently be set either to a positive or negative value, equal to the width of the panel in px),
          // to trigger the animated "closing" transition
          self.translateX = 0;
        }
      }

      // Change `[data-display]` value to "hide",
      // so that `onTransitionEnd()` will be able to determine that the panel just finished "closing",
      // and to trigger the animated closing transition if panel is a "push" type
      self.display = 'hide';
    });
  }


  /**
   * Update the component to use a new element or reparse from
   * the existing element.
   * @param {Element} el Optional
   * @param {Object} params Optional
   * @public
   */
  update(el, params = {}) {
    this._removeEventListeners();

    this._cacheElements.call(this, el || this.el, params);
    this._addEventListeners();

    if (params.position) this.position = params.position;

    return this;
  }

}

/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
SideFixedPanel.prototype._whitelistedParams = [
  '_parentPanel',
  'condensedBreakpoint',
  'mobileBreakpoint',
  'scrollTop',
  'onDismiss',
  'onToggle',
  'onOpen',
  'onClose'
];

/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
SideFixedPanel.prototype.defaults = {
  _parentPanel: null,
  el: null,
  scrollTop: true,
  mobileBreakpoint: 'xs',
  condensedBreakpoint: '823',
  onDismiss: () => {},
  onToggle: () => {},
  onOpen: () => {},
  onClose: () => {}
};

mixin(SideFixedPanel.prototype, events);

export default SideFixedPanel;
